import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReactEChartsCore from "echarts-for-react/lib/core";
import { BarChart } from "echarts/charts";
import * as echarts from "echarts/core";
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  GraphicComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { Card } from "react-bootstrap";
import { capitalize, getColor } from "../../../helpers/surveyUtils";
import { motion } from "framer-motion";

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent,
  GraphicComponent,
]);

// Format date to human-readable format like "Jan 15", "Feb 02"
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    return `${month} ${day}`;
  } catch (e) {
    return dateString;
  }
};

// Format template name properly (CSAT 5, NPS 10, etc.)
const formatTemplateName = (templateKey) => {
  if (!templateKey) return "";
  return templateKey
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => {
      // If it's a number, keep as is
      if (!isNaN(word)) return word;
      // If it's an acronym like csat, nps - uppercase it
      if (/^[a-z]+$/i.test(word)) return word.toUpperCase();
      return word;
    })
    .join(' ');
};

const getOptions = (month, graphData) => {
  // More robust data validation: check if data exists, is an array, has items, and items have valid structure
  const hasData =
    graphData &&
    Array.isArray(graphData) &&
    graphData.length > 0 &&
    graphData.some(item =>
      item &&
      typeof item === 'object' &&
      item.recived_date &&
      typeof item.satisfied === 'number' &&
      typeof item.neutral === 'number' &&
      typeof item.dissatisfied === 'number' &&
      typeof item.total_count === 'number' &&
      item.total_count > 0
    );

  return {
    color: ["#22c55e", "#94a3b8", "#ef4444"],
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderColor: getColor("gray-300"),
      borderWidth: 1,
      textStyle: { color: "#333", fontSize: 14 },
      formatter: (params) => {
        if (!hasData) return "No data available";
        const totalCount = params[0].data.total;
        let tooltipContent = `<div style="margin-bottom: 5px;"><strong>Total: ${totalCount}</strong></div>`;
        tooltipContent += `<strong>${month} ${params[0].name}</strong><br/>`;
        tooltipContent += params
          .map(
            (item) => `
            <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${
                item.color
              }; border-radius: 50%; margin-right: 5px;"></span>
              ${item.seriesName}: <strong>${item.value}%</strong> (${Math.round(
              (item.value / 100) * item.data.total
            )} responses)
            </div>
          `
          )
          .join("");
        return tooltipContent;
      },
    },
    legend: {
      data: ["Satisfied", "Neutral", "Dissatisfied"],
      textStyle: { color: "#333", fontSize: 13 },
      itemHeight: 14,
      itemGap: 15,
    },
    xAxis: {
      type: "category",
      data: hasData
        ? graphData
            .filter(item => item && item.total_count > 0)
            .map((item) => formatDate(item.recived_date))
        : [],
      axisLine: { lineStyle: { color: getColor("gray-300") } },
      axisLabel: {
        color: "#555",
        fontSize: 12,
        rotate: 0,
        formatter: (value, index) => {
          if (!hasData) return "No Data";
          const validData = graphData.filter(item => item && item.total_count > 0);
          if (index >= validData.length) return "No Data";
          const totalCount = validData[index].total_count;
          return `${value}\n(${totalCount})`;
        },
      },
    },
    yAxis: {
      type: "value",
      max: 100,
      axisLine: { show: false },
      axisLabel: {
        color: "#555",
        fontSize: 12,
        formatter: (value) => `${value}%`,
      },
      splitLine: { lineStyle: { color: "#ddd", type: "dashed" } },
    },
    series: hasData
      ? [
          {
            name: "Satisfied",
            type: "bar",
            barWidth: "15%",
            data: graphData
              .filter(item => item && item.total_count > 0) // Filter out invalid items
              .map((item) => ({
                value: parseFloat(((item.satisfied || 0) / item.total_count) * 100).toFixed(2),
                total: item.total_count,
              })),
            itemStyle: { color: "#22c55e" },
          },
          {
            name: "Neutral",
            type: "bar",
            barWidth: "15%",
            data: graphData
              .filter(item => item && item.total_count > 0) // Filter out invalid items
              .map((item) => ({
                value: parseFloat(((item.neutral || 0) / item.total_count) * 100).toFixed(2),
                total: item.total_count,
              })),
            itemStyle: { color: "#94a3b8" },
          },
          {
            name: "Dissatisfied",
            type: "bar",
            barWidth: "15%",
            data: graphData
              .filter(item => item && item.total_count > 0) // Filter out invalid items
              .map((item) => ({
                value: parseFloat(((item.dissatisfied || 0) / item.total_count) * 100).toFixed(2),
                total: item.total_count,
              })),
            itemStyle: { color: "#ef4444" },
          },
        ]
      : [],
    grid: {
      left: "5%",
      right: "5%",
      bottom: "10%",
      top: "10%",
      containLabel: true,
    },
    graphic: hasData
      ? []
      : [
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              text: "No Graph Data Available",
              fill: "#999",
              fontSize: 16,
              fontWeight: "bold",
            },
          },
        ],
  };
};

const XSurveyGraph = ({ graphData, selectedTemplate }) => {
  const [month, setMonth] = useState("");

  // Update month based on graphData
  useEffect(() => {
    if (graphData && Array.isArray(graphData) && graphData.length > 0) {
      // Extract month from the first date
      const firstDate = graphData[0]?.recived_date;
      if (firstDate) {
        try {
          const date = new Date(firstDate);
          const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
          ];
          setMonth(monthNames[date.getMonth()] || "");
        } catch (e) {
          setMonth("");
        }
      }
    } else {
      setMonth("");
    }
  }, [graphData]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <Card
        className="h-100 border-0 shadow-sm"
        style={{ borderRadius: "12px", overflow: "hidden" }}
      >
        <Card.Header className="pb-0 pt-3 bg-transparent border-0">
          <h6>{`${formatTemplateName(selectedTemplate)} (Over Time)`}</h6>
        </Card.Header>
        <Card.Body className="pt-2">
          <ReactEChartsCore
            echarts={echarts}
            option={getOptions(month, graphData)}
            style={{ height: "18.4375rem" }}
            selectedTemplate={selectedTemplate}
            key={`graph-${selectedTemplate}-${JSON.stringify(graphData).slice(0, 50)}`}
            notMerge={true}
          />
        </Card.Body>
      </Card>
    </motion.div>
  );
};

XSurveyGraph.propTypes = {
  graphData: PropTypes.arrayOf(
    PropTypes.shape({
      recived_date: PropTypes.string.isRequired,
      satisfied: PropTypes.number.isRequired,
      neutral: PropTypes.number.isRequired,
      dissatisfied: PropTypes.number.isRequired,
      total_count: PropTypes.number.isRequired,
    })
  ).isRequired,
};

export default XSurveyGraph;
